import { ForbiddenException, Injectable } from '@nestjs/common';
import * as crypto from 'crypto';

import { AuthenticatedUserResponse, Loggable } from '@ip6m/server/shared';
import { UserDataService } from '@ip6m/server/data-access-user';
import { OtpDataService, OtpEntity } from '@ip6m/server/data-access-otp';
import { TokenService } from '@ip6m/server/auth';
import { UserNotFoundException } from '@ip6m/server/data-access-user';

@Injectable()
export class OtpService {
  constructor(
    private userDataService: UserDataService,
    private otpDataService: OtpDataService,
    private tokenService: TokenService
  ) {}

  @Loggable
  static createNewOtp({ userId }: { userId: number }) {
    const otpEntity = new OtpEntity();
    otpEntity.userId = userId;
    otpEntity.otp = crypto.randomBytes(24).toString('hex');
    otpEntity.validUntil = new Date(Date.now() + 5 * 60 * 1000);

    return otpEntity;
  }

  /**
   * @throws UserNotFoundException
   */
  @Loggable
  public async sendOtp(email: string): Promise<void> {
    const [user] = await this.userDataService.findByEmail(email);
    if (!user) {
      throw new UserNotFoundException(email);
    }

    const [otp] = await this.otpDataService.findByUserId(user.id);

    if (otp) {
      await this.otpDataService.delete(otp.id);
    }
    const newOtp = OtpService.createNewOtp({ userId: user.id });
    await this.otpDataService.create(newOtp);
  }

  /**
   * @throws UserNotFoundException
   * @throws ForbiddenException
   */
  @Loggable
  async checkOtp(
    email: string,
    otp: string
  ): Promise<AuthenticatedUserResponse> {
    const [user] = await this.userDataService.findByEmail(email);
    if (!user) {
      throw new UserNotFoundException(email);
    }

    const [attachedOtp] = await this.otpDataService.findByUserId(user.id);
    if (!attachedOtp) {
      throw new ForbiddenException();
    }
    if (new Date(attachedOtp.validUntil) <= new Date()) {
      await this.otpDataService.delete(attachedOtp.id);
      throw new ForbiddenException();
    }

    if (attachedOtp.otp !== otp) {
      throw new ForbiddenException();
    }

    await this.otpDataService.delete(attachedOtp.id);
    const idToken = this.tokenService.signIdToken({
      sub: user.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone,
      roles: user.roles
    });
    const accessToken = this.tokenService.signAccessToken(user.id.toString());

    return new AuthenticatedUserResponse({ idToken, accessToken });
  }
}
